import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Label } from '../components/ui/label';
import { 
  Calendar as CalendarIcon, 
  Plus, 
  Filter,
  ChevronLeft,
  ChevronRight,
  User,
  AlertCircle
} from 'lucide-react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, addMonths, subMonths } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SchedulePage() {
  const { user, isAdmin } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [assignments, setAssignments] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [functions, setFunctions] = useState([]);
  const [users, setUsers] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterSector, setFilterSector] = useState('all');
  const [filterOnlyMine, setFilterOnlyMine] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [newAssignment, setNewAssignment] = useState({
    user_id: '',
    sector_id: '',
    function_id: '',
  });

  useEffect(() => {
    fetchData();
  }, [currentDate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const month = currentDate.getMonth() + 1;
      const year = currentDate.getFullYear();
      
      const [assignmentsRes, sectorsRes, functionsRes, usersRes, schedulesRes] = await Promise.all([
        fetch(`${API_URL}/api/assignments?schedule_id=`, { credentials: 'include' }),
        fetch(`${API_URL}/api/sectors`, { credentials: 'include' }),
        fetch(`${API_URL}/api/functions`, { credentials: 'include' }),
        fetch(`${API_URL}/api/users`, { credentials: 'include' }),
        fetch(`${API_URL}/api/schedules?month=${month}&year=${year}`, { credentials: 'include' }),
      ]);

      if (assignmentsRes.ok) setAssignments(await assignmentsRes.json());
      if (sectorsRes.ok) setSectors(await sectorsRes.json());
      if (functionsRes.ok) setFunctions(await functionsRes.json());
      if (usersRes.ok) setUsers(await usersRes.json());
      if (schedulesRes.ok) setSchedules(await schedulesRes.json());
    } catch (error) {
      console.error('Error fetching schedule data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const getSectorName = (sectorId) => {
    const sector = sectors.find(s => s.sector_id === sectorId);
    return sector?.name || 'N/A';
  };

  const getFunctionName = (functionId) => {
    const func = functions.find(f => f.function_id === functionId);
    return func?.name || 'N/A';
  };

  const getUserName = (userId) => {
    if (!userId) return null;
    const u = users.find(usr => usr.user_id === userId);
    return u?.name || 'N/A';
  };

  const getSectorColorClass = (sectorId) => {
    const sector = sectors.find(s => s.sector_id === sectorId);
    const colorMap = {
      orange: 'border-l-orange-500 bg-orange-500/5',
      cyan: 'border-l-cyan-500 bg-cyan-500/5',
      emerald: 'border-l-emerald-500 bg-emerald-500/5',
      amber: 'border-l-amber-500 bg-amber-500/5',
    };
    return colorMap[sector?.color] || 'border-l-zinc-500';
  };

  const getSectorBadgeClass = (sectorId) => {
    const sector = sectors.find(s => s.sector_id === sectorId);
    const colorMap = {
      orange: 'text-orange-500 bg-orange-500/10',
      cyan: 'text-cyan-500 bg-cyan-500/10',
      emerald: 'text-emerald-500 bg-emerald-500/10',
      amber: 'text-amber-500 bg-amber-500/10',
    };
    return colorMap[sector?.color] || 'text-zinc-400 bg-zinc-800';
  };

  // Get days of current month
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Filter assignments
  const filteredAssignments = assignments.filter(a => {
    if (filterSector !== 'all' && a.sector_id !== filterSector) return false;
    if (filterOnlyMine && a.user_id !== user?.user_id) return false;
    return true;
  });

  // Get assignments for a specific date
  const getAssignmentsForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return filteredAssignments.filter(a => a.date === dateStr);
  };

  const mobileDaysWithAssignments = daysInMonth.filter((day) => (
    getAssignmentsForDate(day).length > 0
  ));

  // Group assignments by sector for a date
  const getAssignmentsBySector = (date) => {
    const dateAssignments = getAssignmentsForDate(date);
    const grouped = {};
    sectors.forEach(sector => {
      grouped[sector.sector_id] = dateAssignments.filter(a => a.sector_id === sector.sector_id);
    });
    return grouped;
  };

  const handleCreateAssignment = async () => {
    if (!selectedDate || !newAssignment.sector_id || !newAssignment.function_id) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    // Get or create schedule for this month
    let scheduleId = schedules[0]?.schedule_id;
    if (!scheduleId) {
      // Create schedule
      const scheduleRes = await fetch(`${API_URL}/api/schedules`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: `Escala ${format(currentDate, 'MMMM yyyy', { locale: ptBR })}`,
          month: currentDate.getMonth() + 1,
          year: currentDate.getFullYear(),
        }),
      });
      if (scheduleRes.ok) {
        const newSchedule = await scheduleRes.json();
        scheduleId = newSchedule.schedule_id;
        setSchedules([...schedules, newSchedule]);
      } else {
        toast.error('Erro ao criar escala');
        return;
      }
    }

    try {
      const response = await fetch(`${API_URL}/api/assignments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          schedule_id: scheduleId,
          user_id: newAssignment.user_id || null,
          sector_id: newAssignment.sector_id,
          function_id: newAssignment.function_id,
          date: format(selectedDate, 'yyyy-MM-dd'),
        }),
      });

      if (response.ok) {
        toast.success('Atribuição criada com sucesso');
        setDialogOpen(false);
        setNewAssignment({ user_id: '', sector_id: '', function_id: '' });
        fetchData();
      } else {
        toast.error('Erro ao criar atribuição');
      }
    } catch (error) {
      console.error('Error creating assignment:', error);
      toast.error('Erro ao criar atribuição');
    }
  };

  const handleDeleteAssignment = async (assignmentId) => {
    try {
      const response = await fetch(`${API_URL}/api/assignments/${assignmentId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        toast.success('Atribuição removida');
        fetchData();
      } else {
        toast.error('Erro ao remover atribuição');
      }
    } catch (error) {
      console.error('Error deleting assignment:', error);
      toast.error('Erro ao remover atribuição');
    } finally {
      setDeleteConfirmOpen(false);
      setDeleteTarget(null);
    }
  };

  const confirmDelete = (assignmentId) => {
    setDeleteTarget(assignmentId);
    setDeleteConfirmOpen(true);
  };

  const handleAssignUser = async (assignmentId, userId) => {
    try {
      const response = await fetch(`${API_URL}/api/assignments/${assignmentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ user_id: userId || null }),
      });

      if (response.ok) {
        toast.success('Voluntário atribuído');
        fetchData();
      } else {
        toast.error('Erro ao atribuir usuário');
      }
    } catch (error) {
      console.error('Error assigning user:', error);
      toast.error('Erro ao atribuir usuário');
    }
  };

  const renderAssignmentButton = (assignment) => (
    <Popover key={assignment.assignment_id}>
      <PopoverTrigger asChild>
        <button
          className={`w-full text-left text-xs p-1.5 rounded border-l-2 transition-colors ${
            getSectorColorClass(assignment.sector_id)
          } ${
            assignment.user_id === user?.user_id
              ? 'ring-1 ring-blue-500/50'
              : ''
          } ${
            !assignment.user_id
              ? 'border-dashed border-red-500/50 animate-pulse-slow'
              : ''
          } hover:bg-zinc-700/30`}
          data-testid={`assignment-${assignment.assignment_id}`}
        >
          <span className={`block truncate ${
            !assignment.user_id ? 'text-red-400 font-medium' : 'text-zinc-300'
          }`}>
            {`${getUserName(assignment.user_id) || 'VAGA'}${assignment.sector_id ? ` • ${getSectorName(assignment.sector_id)}` : ''}${assignment.function_id ? ` • ${getFunctionName(assignment.function_id)}` : ''}`}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3 bg-zinc-900 border-zinc-800">
        <div className="space-y-3">
          <div>
            <p className="text-sm font-medium text-zinc-200">
              {getUserName(assignment.user_id) || 'VAGA'}
            </p>
            <div className="flex gap-2 mt-1">
              <Badge className={getSectorBadgeClass(assignment.sector_id)}>
                {getSectorName(assignment.sector_id)}
              </Badge>
              <Badge variant="outline" className="border-zinc-700 text-zinc-400">
                {getFunctionName(assignment.function_id)}
              </Badge>
            </div>
          </div>

          {isAdmin && (
            <div className="space-y-2 pt-2 border-t border-zinc-800">
              <Select
                value={assignment.user_id || 'vaga'}
                onValueChange={(v) => handleAssignUser(assignment.assignment_id, v === 'vaga' ? '' : v)}
              >
                <SelectTrigger className="bg-zinc-950 border-zinc-800 text-xs h-8">
                                          <SelectValue placeholder="Atribuir voluntário" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="vaga">VAGA</SelectItem>
                  {users.map(u => (
                    <SelectItem key={u.user_id} value={u.user_id}>
                      {u.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="destructive"
                size="sm"
                className="w-full h-8 text-xs"
                onClick={() => confirmDelete(assignment.assignment_id)}
                data-testid={`delete-assignment-${assignment.assignment_id}`}
              >
                Remover
              </Button>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="schedule-page">
        {/* Header */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0">
            <h1 className="text-3xl font-bold text-zinc-50 tracking-tight">Escala</h1>
            <p className="text-zinc-400 mt-1">Visualize e gerencie as designações</p>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
            {/* Month Navigation */}
            <div className="flex w-full items-center gap-2 bg-zinc-900 rounded-lg p-1 border border-zinc-800 sm:w-auto">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => setCurrentDate(subMonths(currentDate, 1))}
                className="h-8 w-8"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="flex-1 text-sm font-medium text-zinc-200 text-center sm:min-w-[120px]">
                {format(currentDate, 'MMMM yyyy', { locale: ptBR })}
              </span>
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => setCurrentDate(addMonths(currentDate, 1))}
                className="h-8 w-8"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>

            {/* Filters */}
            <Select value={filterSector} onValueChange={setFilterSector}>
              <SelectTrigger className="w-full bg-zinc-900 border-zinc-800 sm:w-[140px]" data-testid="filter-sector">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Setor" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem value="all">Todos</SelectItem>
                {sectors.map(sector => (
                  <SelectItem key={sector.sector_id} value={sector.sector_id}>
                    {sector.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              variant={filterOnlyMine ? 'default' : 'outline'}
              onClick={() => setFilterOnlyMine(!filterOnlyMine)}
              className={`w-full sm:w-auto ${filterOnlyMine ? 'bg-white text-black hover:bg-zinc-200' : 'border-zinc-800'}`}
              data-testid="filter-mine"
            >
              <User className="w-4 h-4 mr-2" />
              Minhas
            </Button>

            {isAdmin && (
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="w-full bg-white text-black hover:bg-zinc-200 sm:w-auto" data-testid="add-assignment-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Nova Atribuição
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-zinc-900 border-zinc-800 max-h-[85vh] overflow-hidden sm:max-h-[90vh]">
                  <DialogHeader>
                    <DialogTitle className="text-zinc-100">Nova Atribuição</DialogTitle>
                  </DialogHeader>
                  <div className="flex max-h-full flex-col pt-4">
                    <div className="space-y-4 overflow-y-auto pr-1 pb-24 sm:pb-4">
                    <div className="space-y-2">
                      <Label className="text-zinc-400">Data</Label>
                      <div className="space-y-3">
                        <Button
                          variant="outline"
                          className="w-full justify-start border-zinc-800 bg-zinc-950"
                          onClick={() => setCalendarOpen(!calendarOpen)}
                        >
                          <CalendarIcon className="w-4 h-4 mr-2" />
                          {selectedDate ? format(selectedDate, 'dd/MM/yyyy') : 'Selecione uma data'}
                        </Button>

                        {calendarOpen && (
                          <div className="flex justify-center rounded-lg border border-zinc-800 bg-zinc-950 p-2">
                            <Calendar
                              mode="single"
                              selected={selectedDate}
                              onSelect={(date) => {
                                setSelectedDate(date);
                                setCalendarOpen(false);
                              }}
                              className="mx-auto"
                              initialFocus
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-zinc-400">Setor *</Label>
                      <Select 
                        value={newAssignment.sector_id} 
                        onValueChange={(v) => setNewAssignment({...newAssignment, sector_id: v})}
                      >
                        <SelectTrigger className="bg-zinc-950 border-zinc-800">
                          <SelectValue placeholder="Selecione um setor" />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800">
                          {sectors.map(sector => (
                            <SelectItem key={sector.sector_id} value={sector.sector_id}>
                              {sector.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-zinc-400">Função *</Label>
                      <Select 
                        value={newAssignment.function_id} 
                        onValueChange={(v) => setNewAssignment({...newAssignment, function_id: v})}
                      >
                        <SelectTrigger className="bg-zinc-950 border-zinc-800">
                          <SelectValue placeholder="Selecione uma função" />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800">
                          {functions.map(func => (
                            <SelectItem key={func.function_id} value={func.function_id}>
                              {func.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-zinc-400">Voluntário (deixe vazio para VAGA)</Label>
                      <Select 
                        value={newAssignment.user_id || "vaga"} 
                        onValueChange={(v) => setNewAssignment({...newAssignment, user_id: v === "vaga" ? "" : v})}
                      >
                        <SelectTrigger className="bg-zinc-950 border-zinc-800">
                          <SelectValue placeholder="Selecione um voluntário (opcional)" />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800">
                          <SelectItem value="vaga">VAGA (sem voluntário)</SelectItem>
                          {users.map(u => (
                            <SelectItem key={u.user_id} value={u.user_id}>
                              {u.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    </div>
                    <div className="absolute inset-x-0 bottom-0 border-t border-zinc-800 bg-zinc-900/95 p-6 backdrop-blur sm:static sm:border-t-0 sm:bg-transparent sm:p-0 sm:pt-4">
                    <Button 
                      onClick={handleCreateAssignment}
                      className="w-full bg-white text-black hover:bg-zinc-200"
                      data-testid="confirm-assignment-btn"
                    >
                      Criar Atribuição
                    </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        {/* Schedule Grid */}
        <Card className="bg-zinc-900 border-zinc-800 overflow-hidden">
          <CardContent className="p-0">
            <div className="md:hidden divide-y divide-zinc-800">
              {mobileDaysWithAssignments.length > 0 ? mobileDaysWithAssignments.map((day) => {
                const dayAssignments = getAssignmentsForDate(day);
                const isToday = isSameDay(day, new Date());
                const hasOpenSlots = dayAssignments.some(a => !a.user_id);
                const hasMyAssignment = dayAssignments.some(a => a.user_id === user?.user_id);

                return (
                  <div
                    key={`mobile-${day.toISOString()}`}
                    className={`p-4 space-y-3 ${isToday ? 'bg-zinc-800/20' : ''}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm text-zinc-400">
                          {format(day, "EEEE", { locale: ptBR })}
                        </p>
                        <p className="text-lg font-semibold text-zinc-100">
                          {format(day, "dd 'de' MMMM", { locale: ptBR })}
                        </p>
                      </div>
                      <div className="flex gap-1">
                        {hasOpenSlots && (
                          <Badge className="text-red-400 bg-red-400/10 text-xs px-1.5">
                            <AlertCircle className="w-3 h-3" />
                          </Badge>
                        )}
                        {hasMyAssignment && (
                          <Badge className="bg-blue-600/20 text-blue-400 text-xs px-1.5">
                            <User className="w-3 h-3" />
                          </Badge>
                        )}
                      </div>
                    </div>

                    {dayAssignments.length > 0 ? (
                      <div className="space-y-2">
                        {dayAssignments.map((assignment) => renderAssignmentButton(assignment))}
                      </div>
                    ) : (
                      <p className="text-sm text-zinc-500">Nenhuma atribuição neste dia.</p>
                    )}
                  </div>
                );
              }) : (
                <div className="p-6 text-center">
                  <p className="text-sm text-zinc-500">
                    Nenhuma designação encontrada neste mês com os filtros atuais.
                  </p>
                </div>
              )}
            </div>

            <div className="hidden md:block overflow-x-auto">
              <div className="min-w-[800px]">
                {/* Days Header */}
                <div className="grid grid-cols-7 border-b border-zinc-800">
                  {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map((day, i) => (
                    <div key={day} className="p-3 text-center text-sm font-medium text-zinc-400 border-r border-zinc-800 last:border-r-0">
                      {day}
                    </div>
                  ))}
                </div>

                {/* Calendar Grid */}
                <div className="grid grid-cols-7">
                  {/* Empty cells for days before month start */}
                  {Array.from({ length: monthStart.getDay() }).map((_, i) => (
                    <div key={`empty-start-${i}`} className="min-h-[120px] bg-zinc-950/50 border-r border-b border-zinc-800" />
                  ))}

                  {/* Days of the month */}
                  {daysInMonth.map((day) => {
                    const dayAssignments = getAssignmentsForDate(day);
                    const isToday = isSameDay(day, new Date());
                    const hasOpenSlots = dayAssignments.some(a => !a.user_id);
                    const hasMyAssignment = dayAssignments.some(a => a.user_id === user?.user_id);

                    return (
                      <div 
                        key={day.toISOString()}
                        className={`min-h-[120px] border-r border-b border-zinc-800 last:border-r-0 p-2 ${
                          isToday ? 'bg-zinc-800/30' : ''
                        }`}
                      >
                        <div className={`flex items-center justify-between mb-2`}>
                          <span className={`text-sm font-medium ${
                            isToday ? 'text-white bg-white/20 rounded-full w-7 h-7 flex items-center justify-center' : 'text-zinc-400'
                          }`}>
                            {format(day, 'd')}
                          </span>
                          <div className="flex gap-1">
                            {hasOpenSlots && (
                              <Badge className="text-red-400 bg-red-400/10 text-xs px-1">
                                <AlertCircle className="w-3 h-3" />
                              </Badge>
                            )}
                            {hasMyAssignment && (
                              <Badge className="bg-blue-600/20 text-blue-400 text-xs px-1">
                                <User className="w-3 h-3" />
                              </Badge>
                            )}
                          </div>
                        </div>

                        <div className="space-y-1">
                          {dayAssignments.slice(0, 3).map((assignment) => (
                            renderAssignmentButton(assignment)
                          ))}
                          {dayAssignments.length > 3 && (
                            <span className="text-xs text-zinc-500 pl-1">
                              +{dayAssignments.length - 3} mais
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {/* Empty cells for days after month end */}
                  {Array.from({ length: 6 - monthEnd.getDay() }).map((_, i) => (
                    <div key={`empty-end-${i}`} className="min-h-[120px] bg-zinc-950/50 border-r border-b border-zinc-800" />
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
          <AlertDialogContent className="bg-zinc-900 border-zinc-800">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-zinc-100">Confirmar exclusão</AlertDialogTitle>
              <AlertDialogDescription className="text-zinc-400">
                Tem certeza que deseja remover esta atribuição? Esta ação não pode ser desfeita.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-zinc-300 hover:bg-zinc-700">
                Cancelar
              </AlertDialogCancel>
              <AlertDialogAction 
                onClick={() => deleteTarget && handleDeleteAssignment(deleteTarget)}
                className="bg-red-600 text-white hover:bg-red-700"
              >
                Remover
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
